/**
 * Sign-in integrity signals.
 *
 * Collects how the credentials actually reached the form (real key presses,
 * paste, browser autofill, or values injected by a script), how the form was
 * submitted, and a few environment markers. The server re-checks everything;
 * nothing here decides on its own.
 */

const FIELDS = ['username', 'password'];
const MAX_SAMPLES = 64;

const now = () =>
  window.performance && performance.now ? performance.now() : Date.now();

function environmentFlags() {
  const f = [];
  const w = window;
  const n = w.navigator || {};
  try {
    if (n.webdriver) f.push('wd');
    if (w.Cypress || w.parent?.Cypress) f.push('cy');
    if (w.callPhantom || w._phantom || w.phantom) f.push('ph');
    if (w.__nightmare) f.push('nm');
    if (w.domAutomation || w.domAutomationController) f.push('da');
    if (/HeadlessChrome|PhantomJS|Electron\//i.test(n.userAgent || '')) {
      f.push('ua');
    }
    const keys = Object.getOwnPropertyNames(w).concat(
      Object.getOwnPropertyNames(w.document),
    );
    if (
      keys.some((k) =>
        /^(\$?cdc_|\$wdc_|__webdriver|__selenium|__fxdriver|__driver_|_selenium|_Selenium_IDE|calledSelenium|__playwright|__pw|__puppeteer)/.test(
          k,
        ),
      )
    ) {
      f.push('gk');
    }
    const root = w.document.documentElement;
    if (['webdriver', 'selenium', 'driver'].some((a) => root.hasAttribute(a))) {
      f.push('da2');
    }
    if (w.outerWidth === 0 && w.outerHeight === 0) f.push('hl');
    if (n.languages !== undefined && n.languages.length === 0) f.push('lg');
  } catch (e) {
    f.push('ex');
  }
  return f;
}

function isAutofilled(el) {
  for (const sel of [':autofill', ':-webkit-autofill']) {
    try {
      if (el.matches(sel)) return true;
    } catch (e) {
      // selector not supported by this browser
    }
  }
  return false;
}

export function createLoginGuard(root) {
  const start = now();
  const state = {
    m: 0,
    tc: 0,
    tab: 0,
    sub: 'none',
    subAt: 0,
    f: [],
    fields: {},
  };
  FIELDS.forEach((name) => {
    state.fields[name] = {
      k: 0,
      ty: 0,
      sy: 0,
      pa: 0,
      au: 0,
      last: -1e9,
      prev: 0,
      iv: [],
    };
  });

  const fieldOf = (t) =>
    t && t.name && state.fields[t.name] && root.contains(t)
      ? state.fields[t.name]
      : null;

  const onKey = (e) => {
    const fld = fieldOf(e.target);
    if (!e.isTrusted) {
      if (fld || root.contains(e.target)) state.f.push('uk');
      return;
    }
    if (e.key === 'Tab') state.tab += 1;
    if (e.key === 'Enter' && root.contains(e.target)) {
      state.sub = 'enter';
      state.subAt = now();
    }
    if (!fld) return;
    const t = now();
    fld.k += 1;
    fld.last = t;
    if (e.key && (e.key.length === 1 || e.key === 'Unidentified')) {
      if (fld.prev && fld.iv.length < MAX_SAMPLES) {
        fld.iv.push(Math.round(t - fld.prev));
      }
      fld.prev = t;
    }
  };

  const onInput = (e) => {
    const fld = fieldOf(e.target);
    if (!fld) return;
    if (!e.isTrusted) {
      fld.sy += 1;
      return;
    }
    const type = e.inputType || '';
    if (type === 'insertFromPaste' || type === 'insertFromDrop') {
      fld.pa += 1;
    } else if (type === 'insertCompositionText') {
      fld.ty += 1;
    } else if (type === 'insertText') {
      if (now() - fld.last < 150) fld.ty += 1;
      else fld.sy += 1;
    } else if (!type || type === 'insertReplacementText') {
      fld.au += 1;
    }
  };

  const onMove = (e) => {
    if (e.isTrusted) state.m += 1;
  };
  const onTouch = (e) => {
    if (e.isTrusted) state.tc += 1;
  };
  const onClick = (e) => {
    const btn = e.target && e.target.closest && e.target.closest('button');
    if (!btn || !root.contains(btn) || btn.type !== 'submit') return;
    if (!e.isTrusted) {
      state.f.push('uc');
      return;
    }
    state.sub = 'click';
    state.subAt = now();
  };

  const opts = {capture: true, passive: true};
  const listeners = [
    ['keydown', onKey],
    ['input', onInput],
    ['mousemove', onMove],
    ['pointermove', onMove],
    ['touchstart', onTouch],
    ['click', onClick],
  ];
  listeners.forEach(([ev, fn]) => document.addEventListener(ev, fn, opts));

  function snapshot() {
    const out = {
      v: 1,
      dt: Math.round(now() - start),
      m: Math.min(state.m, 9999),
      tc: Math.min(state.tc, 999),
      tab: Math.min(state.tab, 999),
      sub: now() - state.subAt < 2500 ? state.sub : 'none',
      f: Array.from(new Set(state.f.concat(environmentFlags()))),
    };
    FIELDS.forEach((name) => {
      const fld = state.fields[name];
      const el = root.querySelector(`input[name="${name}"]`);
      const len = el ? String(el.value || '').length : 0;
      out[name[0]] = {
        len,
        k: fld.k,
        ty: fld.ty,
        sy: fld.sy,
        pa: fld.pa,
        au: fld.au + (el && isAutofilled(el) ? 1 : 0),
        iv: fld.iv,
      };
    });
    return out;
  }

  async function sha256Hex(text) {
    const data = new TextEncoder().encode(text);
    const buf = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }

  /**
   * @param {string} nonce issued with the page
   * @returns {Promise<{g: string, c: string}>}
   */
  async function seal(nonce) {
    const g = btoa(JSON.stringify(snapshot()));
    if (!nonce || !window.crypto || !crypto.subtle) return {g, c: ''};
    for (let c = 0; c < 2000000; c += 1) {
      const h = await sha256Hex(`${nonce}.${g}.${c}`);
      if (h.startsWith('000')) return {g, c: String(c)};
    }
    return {g, c: ''};
  }

  function destroy() {
    listeners.forEach(([ev, fn]) => document.removeEventListener(ev, fn, opts));
  }

  return {seal, destroy};
}
